import { ApiResponseJuspayMeta, OnPayExtraParams } from "@swiggy-private/payment-types";
import { ReactElement } from "react";
import { CodConsentInfo } from "./Cod";
import { IExpenseCodeData } from "./BillToCompany";
import { AnalyticsContextValue } from "@swiggy-private/checkout-core";

export interface PaymentGroup<T extends PaymentGroupMeta, U extends PaymentMethod<PaymentMethodMeta>> {
    /** Contains all the payment details in order to render payment group */
    paymentData: PaymentGroupData<T, U>;
    /** Contains all the action and values in order to check and process state of payment group */
    paymentActions: PaymentGroupActions<U>;
    /** All application meta swgy_options informations */
    options: PaymentGroupOptions;
    /** Selected will contains the id for selected payment method  (ex. "wallet:paytm" annotating to "{GROUP_NAME}:{METHOD_NAME}"*/
    selectedPaymentId: string;
    /** Check whether payment is already in progress or not */
    paymentInProgress: boolean;
    /** determines to show group title or not */
    hideGroupTitle?: boolean;
    /** amount to be paid */
    paymentAmount: string;
    isFrictionPage?: boolean;
    paymentGroupBanner?: ReactElement | null;
}

export type PaymentGroupOptions = {};
export enum GroupIconPosition {
    RIGHT = "right",
    LEFT = "left",
}
export interface PaymentGroupMeta {
    bannerMessage?: string;
    iconUrl?: string;
    ctaSubtext?: string;
    disableMessage?: string;
    groupIconUrl?: string;
    groupSubtext?: string;
    groupIconPosition?: GroupIconPosition;
    expenseCodeData?: IExpenseCodeData;
    merchantId?: string;
}

export interface PaymentGroupData<T extends PaymentGroupMeta, U extends PaymentMethod<PaymentMethodMeta>> {
    /** To show the title for Card payment group */
    displayName: string;
    /** Group name to uniuely identify the group / category */
    groupName: string;
    /** To identify whether coupon is applicable on payment group*/
    couponApplicable: boolean;
    /** To identify whether reward is applcable on payment group*/
    rewardApplicable?: boolean;
    /** To identify whether group will fall into friction layout */
    isFrictionHiddenGroup?: boolean;
    /** Contains meta info for card payments*/
    meta?: T;
    /** Contains list of all payment method in given card payment group (i.e list of all debit/credit cards or sodexo cards list)*/
    paymentMethods: U[];
    /** Determine whether this is a preferred group */
    preferredGroup: boolean;
    /** Determines whether the whole payment group is disabled */
    enabled?: boolean;
    /** Determines the length of UPI methods to be shown initially */
    foldSize?: number;
    /** Juspay V3 tenant key for this payment group (e.g. "CHECKOUT", "DASH") */
    tenant?: string;
    /** Juspay meta for this specific payment group (may differ per tenant in V3 multi-tenant setup) */
    juspayMeta?: ApiResponseJuspayMeta;
}

export type PaymentGroupDataDef = PaymentGroupData<PaymentGroupMeta, PaymentMethodDef>;

export interface PaymentGroupActions<U extends PaymentMethod<PaymentMethodMeta>> {
    /** Action to invoke when click on payment method */
    onSelect: (paymentMethodData: U) => void;
    /** Action to invoke place order action with given payment method data */
    onPay: (paymentMethodData: U, extraParams?: OnPayExtraParams, analyticsParams?: { [key: string]: unknown }) => void;
    /** Action to invoke when payment wallet is selected */
    onPaymentWalletLoad?: ({
        walletName,
        orderAmount,
        walletBalance,
    }: {
        walletName: string;
        orderAmount: number;
        walletBalance?: string;
    }) => void;
    /** Action to invoke when payment method is loaded */
    onLoad?: (params: Omit<PaymentMethodLoadImpressionEventPayload, "impressionEvent">) => void;
}

export interface PaymentMethodBalanceInfo {
    isLinked: boolean;
    balance: string | number;
    isOldWalletLinkFlow: boolean;
    mobileNumber: string;
}

export interface PaymentMethod<T extends PaymentMethodMeta> {
    /** name of payment method */
    name: string;
    /** display value of payment method */
    displayName: string;
    /** payment code to identify payment method */
    paymentCode: string;
    /** To check whether the payment method is enabled to transact */
    enabled: boolean;
    /** Group name in which this payment method exist */
    groupName: string;
    /** Whether coupon is applicable on the payment method */
    couponApplicable: boolean;
    /** Whether reward is applicable on the payment method */
    rewardApplicable: boolean;
    /** Whether the payment method is preferred payment or not */
    preferred?: boolean;
    /** Contains meta information regarding method data such as iconUrl */
    meta: T;
    /** Is split play enabled on payment method */
    splitPayEnabled: boolean;
    /** Preferred payment priority */
    preferredPriority?: number;
    /** To store balance and link info for applicable payment methods e.g., paytm, sodexo */
    balanceInfo?: PaymentMethodBalanceInfo;
    upiAppName?: string;
    isThirdPartyIntentApp?: boolean;

    // Will come in case of fallback methods api
    splitPaySelected?: boolean;
    splitPayToggleEditable?: boolean;
    splitPayInlineMessage?: string;
    inlineMessage?: string;
    payButtonEntity?: {
        inlineMessage: string;
        orderAmount: number;
    };
    balanceData?: {
        currency: string;
        units: number;
        nanos: number;
    };
    lastUsedPm?: boolean;
    groupMeta?: {
        merchantId?: string;
    };
    discount?: string;
    discounts?: string[];
    payableAmount?: string;
    tooltip?: boolean;
    tooltipText?: string;

    // will come for twid
    poweredBy?: string;
    balance?: string;
}

export type PaymentMethodDef = PaymentMethod<PaymentMethodMeta>;

export interface PaymentMethodProps {
    id: string;
    selected: boolean;
    isPaymentGroupEnabled?: boolean;
    "data-testid"?: string;
}

export interface PaymentMethodMeta {
    /** Icon url to render payment method image */
    iconUrl?: string;
    /** Meta text to render under payment method name */
    promoMsg?: string;
    /** If `disabled`, the message to render the reason */
    disableMessage?: string;
    paymentGroupBannerData?: string;
    cardIssuer?: string;
    /** Adding the simpl redirection url to check if the simpl account is linked or not */
    simplRedirectionUrl?: string;
    /** Reference id for the payment method */
    referenceId?: string;
    /** Whether the payment method is a new one */
    newBadgeEnabled?: boolean;
    /** Half card info for consent cod */
    halfCardInfo?: CodConsentInfo;
    /** Whether the payment method is to be selected by default on render */
    isSelected?: boolean;
    /** Selection tag text (Recommended) for the payment method */
    selectionTagText?: string;
    /** will come for new amazon pay sdk */
    isNewApaySdk?: boolean;
    /** whether the amazon pay is linked */
    isApayLinked?: boolean;
    /** the message to show when the amazon pay is delinked */
    apayDelinkMethodMessage?: string;
    /** the message to show when the amazon pay is delinked */
    apayDelinkGroupMessage?: string;
    /** will come for amazon pay later */
    isApayLaterLinked?: boolean;
    keyFactStatementUrl?: string;
    merchantId?: string;
}

export interface PaymentMethodLoadImpressionEventPayload {
    methodName: string;
    orderAmount?: number;
    methodSpecificData: Record<string, string>;
    impressionEvent: AnalyticsContextValue["impressionEvent"];
}
